/* Copyright (c) 2020-2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { When } from "react-if";
import { Button, ModalConfirm } from "@nordcloud/gnui";
import { useDisclosure } from "~/hooks";
import { useDeleteRoleBinding } from "~/views/permissions/hooks/useDeleteRoleBinding/useDeleteRoleBinding";
import { useDeleteUser } from "~/views/permissions/Users/hooks/useDeleteUser/useDeleteUser";

type Props = {
  email: string;
  roleIds: string[];
  onSuccess?: () => void;
  hasSystemRoles: boolean;
  refetchUsers: () => void;
};

export function BindingRolesDelete({
  email,
  roleIds,
  onSuccess,
  hasSystemRoles,
  refetchUsers,
}: Props) {
  const { isOpen, close, open: openModal } = useDisclosure(false);

  const [deleteRoleBinding] = useDeleteRoleBinding({
    onSuccess: () => {
      close();
      onSuccess?.();
    },
    onError: () => {
      close();
    },
  });

  const [deleteUserLastLoginDate] = useDeleteUser({
    onError: () => {
      close();
    },
  });

  async function deleteUserRoles(ids: string[]) {
    return Promise.all(ids.map((id) => deleteRoleBinding(id)));
  }

  async function onDelete(ids: string[]) {
    await deleteUserRoles(ids);
    await deleteUserLastLoginDate(email);
    refetchUsers();
  }

  return (
    <>
      <Button
        aria-label="delete button"
        icon="trash"
        size="md"
        severity="low"
        onClick={openModal}
        disabled={hasSystemRoles}
      />
      <When condition={isOpen}>
        <ModalConfirm
          onClose={close}
          isOpen={isOpen}
          confirm={() => onDelete(roleIds)}
          actionLabel="Delete"
          contentLabel={`Delete User`}
        >
          Are you sure you want to delete User <b>{`${email}`}</b>?
        </ModalConfirm>
      </When>
    </>
  );
}
